/**
 * Junção manual de requisições (ADR-0074).
 *
 * O comprador escolhe qual das selecionadas fica como PRINCIPAL; a prévia do
 * backend diz o que vai acontecer ("3 itens entram na REQ-20; ela volta para
 * A cotar") e só então ele confirma. A prévia não grava nada.
 */
import React, { useEffect, useState } from "react";
import { FiGitMerge } from "react-icons/fi";

import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import { useAuth } from "../../../context/AuthContext";
import { consolidarRequisicoes, previaConsolidacao } from "../../../services/rqc";
import { SITUACAO_LABEL } from "../../../services/requisicoesCompraUi";
import { Mono } from "../_shared/quadro/styles";
import { ModalCaixa, ModalOverlay, Rotulo, StatusPill, TagOrigem } from "./ui";

function textoDaPrevia(p) {
  const incorporadas = p.absorvidas.map((a) => a.numero).join(", ");
  const itens = `${p.itens_movidos} ${p.itens_movidos === 1 ? "item entra" : "itens entram"} na ${p.principal.numero}`;
  const situacao = p.recua
    ? ` Ela volta para "${SITUACAO_LABEL[p.situacao_depois] || p.situacao_depois}", porque recebe itens ainda a cotar.`
    : "";
  return `${incorporadas} ${p.absorvidas.length === 1 ? "fica" : "ficam"} como consolidada${p.absorvidas.length === 1 ? "" : "s"}; ${itens}, cada um identificado pela requisição de onde veio. Itens iguais ficam lado a lado, sem somar.${situacao} Dá para desfazer enquanto esses itens não tiverem ordem de compra.`;
}

export default function JuntarModal({ open, requisicoes, onCancelar, onJuntada }) {
  const { request } = useAuth();
  const [principalId, setPrincipalId] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [executando, setExecutando] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Sugere como principal a que o comprador ja esta trabalhando (a mais
    // adiantada no fluxo); o resto e incorporado.
    const ordem = ["AGUARDANDO_ENTREGA", "APROVADA", "EM_COTACAO", "RASCUNHO"];
    const sugerida = [...requisicoes].sort((a, b) => ordem.indexOf(a.situacao) - ordem.indexOf(b.situacao))[0];
    setPrincipalId(sugerida?.id || null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !principalId) return undefined;
    let vivo = true;
    const incorporar = requisicoes.filter((r) => r.id !== principalId).map((r) => r.id);
    setCarregando(true);
    setErro("");
    setPrevia(null);
    previaConsolidacao(request, principalId, incorporar)
      .then((p) => { if (vivo) setPrevia(p); })
      .catch((e) => { if (vivo) setErro(e?.payload?.detalhe || e?.message || "Não foi possível conferir a junção."); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [open, principalId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  async function confirmar() {
    const incorporar = requisicoes.filter((r) => r.id !== principalId).map((r) => r.id);
    setExecutando(true);
    setErro("");
    try {
      const principal = await consolidarRequisicoes(request, principalId, incorporar);
      await onJuntada?.(principal);
    } catch (e) {
      setErro(e?.payload?.detalhe || e?.message || "Não foi possível juntar as requisições.");
    } finally {
      setExecutando(false);
    }
  }

  return (
    <ModalOverlay onMouseDown={(e) => { if (e.target === e.currentTarget && !executando) onCancelar?.(); }}>
      <ModalCaixa role="dialog" aria-modal="true" aria-labelledby="juntar-titulo">
        <h3 id="juntar-titulo">
          <FiGitMerge size={18} style={{ color: "var(--accent)" }} />
          Juntar {requisicoes.length} requisições
        </h3>
        <div>
          <Rotulo>Qual fica como principal?</Rotulo>
          <div style={{ display: "grid", gap: 6 }}>
            {requisicoes.map((r) => (
              <label key={r.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="juntar-principal"
                  checked={principalId === r.id}
                  onChange={() => setPrincipalId(r.id)}
                  disabled={executando}
                />
                <Mono $size={12}>{r.numero}</Mono>
                <StatusPill situacao={r.situacao} />
                <TagOrigem origem={r.origem} label={r.origem_label} curta />
                <span style={{ color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.itens_total} itens
                </span>
              </label>
            ))}
          </div>
        </div>
        <p aria-live="polite" style={{ minHeight: 40 }}>
          {carregando ? <MiniSpinner size={13} label="Conferindo" /> : null}
          {!carregando && previa ? textoDaPrevia(previa) : null}
        </p>
        {erro ? <p style={{ color: "var(--danger-text)" }}>{erro}</p> : null}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn--secondary" onClick={onCancelar} disabled={executando}>Voltar</button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={confirmar}
            disabled={!previa || carregando || executando}
          >
            {executando ? <MiniSpinner size={13} label="Aguarde" /> : null}
            Juntar
          </button>
        </div>
      </ModalCaixa>
    </ModalOverlay>
  );
}
